/* eslint react/react-in-jsx-scope: "off" */
import React, { useEffect, useRef } from "react";
import { Form, InputGroup, Button } from "react-bootstrap";
import { Datepicker } from "vanillajs-datepicker";
import "vanillajs-datepicker/css/datepicker-bs5.css";

const toISO = (val) => {
  if (!val) return "";
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const toDateOrUndef = (s) => (s ? new Date(s) : undefined);

/**
 * Props:
 *  - label, name, value (yyyy-mm-dd), onChange({target:{name,value}})
 *  - error (string), required (bool), min, max (yyyy-mm-dd),
 *  - startView: 'days' | 'months' | 'years'
 */
export default function BootstrapDate({
  label,
  name,
  value,
  onChange,
  error,
  required,
  min,
  max,
  placeholder = "YYYY-MM-DD",
  startView = "days",
}) {
  const inputRef = useRef(null);
  const dpRef = useRef(null);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    if (dpRef.current && typeof dpRef.current.destroy === "function") {
      dpRef.current.destroy();
    }

    const dp = new Datepicker(input, {
      autohide: true,
      format: "yyyy-mm-dd",
      startView,
      minDate: toDateOrUndef(min),
      maxDate: toDateOrUndef(max),
      buttonClass: "btn btn-outline-secondary",
    });
    dpRef.current = dp;

    const onChangeDate = (ev) => {
      const picked = ev?.detail?.date || null;
      const iso = toISO(picked);
      if (iso && typeof onChange === "function") {
        onChange({ target: { name, value: iso, type: "text" } });
      }
    };

    input.addEventListener("changeDate", onChangeDate);
    return () => {
      input.removeEventListener("changeDate", onChangeDate);
      if (dpRef.current && typeof dpRef.current.destroy === "function") {
        dpRef.current.destroy();
      }
      dpRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, onChange, min, max, startView]);

  useEffect(() => {
    const dp = dpRef.current;
    if (!dp) return;
    if (value) dp.setDate(value, { render: true });
    else if (inputRef.current) inputRef.current.value = "";
  }, [value]);

  const openCalendar = () => {
    if (dpRef.current && typeof dpRef.current.show === "function") dpRef.current.show();
  };

  return (
    <Form.Group>
      {label && (
        <Form.Label>
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
      )}
      <InputGroup>
        <Form.Control
          ref={inputRef}
          name={name}
          value={value || ""}
          onChange={(e) =>
            onChange?.({ target: { name, value: e.target.value, type: "text" } })
          }
          placeholder={placeholder}
          isInvalid={!!error}
          readOnly
        />
        <Button variant="outline-secondary" type="button" onClick={openCalendar}>
          Pick
        </Button>
        <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
      </InputGroup>
    </Form.Group>
  );
}
